import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { PencilSimple, Plus, Trash } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Switch } from "../../components/ui/switch";
import { api } from "../../lib/api";

type Field = { key: string; label: string; type: "text" | "textarea" | "lines" | "number" | "bool" };

const schemas: Record<string, { label: string; titleKey: string; fields: Field[] }> = {
  projects: {
    label: "Projects",
    titleKey: "title",
    fields: [
      { key: "title", label: "Title", type: "text" },
      { key: "category", label: "Category", type: "text" },
      { key: "location", label: "Location", type: "text" },
      { key: "year", label: "Year", type: "text" },
      { key: "image", label: "Image URL", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "featured", label: "Featured on homepage", type: "bool" },
    ],
  },
  services: {
    label: "Services",
    titleKey: "name",
    fields: [
      { key: "name", label: "Name", type: "text" },
      { key: "slug", label: "Slug", type: "text" },
      { key: "tagline", label: "Tagline", type: "text" },
      { key: "image", label: "Image URL", type: "text" },
      { key: "order", label: "Display Order", type: "number" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "features", label: "Features (one per line)", type: "lines" },
    ],
  },
  careers: {
    label: "Careers",
    titleKey: "title",
    fields: [
      { key: "title", label: "Job Title", type: "text" },
      { key: "type", label: "Employment Type", type: "text" },
      { key: "location", label: "Location", type: "text" },
      { key: "department", label: "Department", type: "text" },
      { key: "description", label: "Description", type: "textarea" },
      { key: "requirements", label: "Requirements (one per line)", type: "lines" },
      { key: "is_open", label: "Accepting applications", type: "bool" },
    ],
  },
  testimonials: {
    label: "Testimonials",
    titleKey: "author",
    fields: [
      { key: "author", label: "Author", type: "text" },
      { key: "role", label: "Role", type: "text" },
      { key: "org", label: "Organization", type: "text" },
      { key: "quote", label: "Quote", type: "textarea" },
    ],
  },
};

export const ContentPanel = () => {
  const [collection, setCollection] = useState("projects");
  const [editing, setEditing] = useState<any | null>(null);
  const qc = useQueryClient();
  const schema = schemas[collection];

  const { data: items = [] } = useQuery<any[]>({
    queryKey: ["admin-content", collection],
    queryFn: async () => (await api.get(`/admin/content/${collection}`)).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["admin-content", collection] });
    qc.invalidateQueries({ queryKey: [collection] });
    qc.invalidateQueries({ queryKey: ["admin-stats"] });
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = { ...editing };
    schema.fields.forEach((f) => {
      if (f.type === "lines" && typeof payload[f.key] === "string")
        payload[f.key] = payload[f.key].split("\n").map((s: string) => s.trim()).filter(Boolean);
      if (f.type === "number") payload[f.key] = Number(payload[f.key]) || 0;
    });
    try {
      if (payload.id) await api.put(`/admin/content/${collection}/${payload.id}`, payload);
      else await api.post(`/admin/content/${collection}`, payload);
      toast.success("Saved.");
      setEditing(null);
      refresh();
    } catch {
      toast.error("Save failed.");
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm("Delete this item permanently?")) return;
    await api.delete(`/admin/content/${collection}/${id}`);
    toast.success("Deleted.");
    refresh();
  };

  const openEditor = (item: any | null) => {
    const base: any = item ? { ...item } : {};
    schema.fields.forEach((f) => {
      if (f.type === "lines") base[f.key] = Array.isArray(base[f.key]) ? base[f.key].join("\n") : base[f.key] || "";
      else if (f.type === "bool") base[f.key] = !!base[f.key];
      else base[f.key] = base[f.key] ?? "";
    });
    setEditing(base);
  };

  return (
    <div data-testid="content-panel">
      <p className="overline-label mb-2">CMS</p>
      <h1 className="font-heading font-medium text-3xl tracking-tight text-ink mb-6">Site Content</h1>

      <div className="flex flex-wrap gap-2 mb-8">
        {Object.entries(schemas).map(([key, s]) => (
          <button
            key={key}
            data-testid={`content-tab-${key}`}
            onClick={() => setCollection(key)}
            className={`px-4 py-2 text-xs font-mono-tech tracking-wider uppercase border transition-colors ${
              collection === key ? "bg-blueprint text-white border-transparent" : "border-line text-sub hover:text-blueprint"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <Button data-testid="content-add-btn" onClick={() => openEditor(null)} className="mb-6 rounded-none bg-[var(--blueprint)] hover:bg-[var(--gold)] hover:text-black">
        <Plus size={15} className="mr-1" /> Add {schema.label.slice(0, -1)}
      </Button>

      <div className="border border-line divide-y divide-[var(--line)]">
        {items.map((item, i) => (
          <div key={item.id} data-testid={`content-item-${i}`} className="flex items-center justify-between gap-4 p-4 bg-panel">
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink truncate">{item[schema.titleKey]}</p>
              <p className="text-xs text-sub font-mono-tech truncate">
                {(item.category || item.tagline || item.department || item.org || "").toString()}
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button data-testid={`content-edit-btn-${i}`} onClick={() => openEditor(item)} className="p-2 border border-line text-sub hover:text-blueprint transition-colors" aria-label="Edit">
                <PencilSimple size={15} />
              </button>
              <button data-testid={`content-delete-btn-${i}`} onClick={() => remove(item.id)} className="p-2 border border-line text-sub hover:text-red-500 transition-colors" aria-label="Delete">
                <Trash size={15} />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="p-8 text-center text-sm text-sub">Nothing here yet.</p>}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-xl rounded-none max-h-[85vh] overflow-y-auto" data-testid="content-editor-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">
              {editing?.id ? "Edit" : "New"} {schema.label.slice(0, -1)}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={save} className="space-y-4">
              {schema.fields.map((f) => (
                <div key={f.key} className="space-y-1.5">
                  {f.type === "bool" ? (
                    <div className="flex items-center justify-between border border-line px-4 py-3">
                      <Label>{f.label}</Label>
                      <Switch
                        data-testid={`editor-field-${f.key}`}
                        checked={!!editing[f.key]}
                        onCheckedChange={(v) => setEditing({ ...editing, [f.key]: v })}
                      />
                    </div>
                  ) : f.type === "textarea" || f.type === "lines" ? (
                    <>
                      <Label>{f.label}</Label>
                      <Textarea
                        data-testid={`editor-field-${f.key}`}
                        rows={f.type === "lines" ? 5 : 4}
                        value={editing[f.key]}
                        onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                        className="rounded-none"
                      />
                    </>
                  ) : (
                    <>
                      <Label>{f.label}</Label>
                      <Input
                        data-testid={`editor-field-${f.key}`}
                        type={f.type === "number" ? "number" : "text"}
                        value={editing[f.key]}
                        onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                        className="rounded-none"
                      />
                    </>
                  )}
                </div>
              ))}
              <Button type="submit" data-testid="editor-save-btn" className="w-full rounded-none bg-[var(--blueprint)] hover:bg-[var(--gold)] hover:text-black">
                Save
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
